import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { cn } from 'src/lib/utils'

const buttonVariants = cva(
	'cursor-pointer w-fit rounded-full text-body [&_span]:text-body inline-flex items-center justify-center gap-4 whitespace-nowrap transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-disabled [&_svg]:pointer-events-none shrink-0 outline-none focus-visible:border-primary  aria-invalid:border-destructive',
	{
		variants: {
			variant: {
				default: 'bg-primary text-black shadow-xs hover:bg-primary/75',
				destructive: 'bg-destructive text-white shadow-xs hover:bg-destructive/90',
				outline: 'border bg-white shadow-xs hover:bg-secondary-grey hover:text-black',
				secondary: 'bg-secondary text-black shadow-xs hover:bg-secondary/80',
				ghost: 'hover:bg-secondary-grey hover:not-disabled:text-black disabled:[&_svg]:text-white',
				link: 'text-black underline-offset-4 hover:underline',
			},
			size: {
				default:
					'h-9 px-4 py-2 has-[>svg]:pr-6 [&.flex-row-reverse]:has-[>svg]:pr-4 [&.flex-row-reverse]:has-[>svg]:pl-6',
				sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
				lg: 'h-10 px-6 has-[>svg]:px-4',
				icon: 'size-9 disabled:bg-transparent disabled:[&_svg]:text-grey disabled:hover:bg-transparent',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
export type ButtonProps = React.ComponentProps<'button'> & ButtonVariants & { asChild?: boolean }

const Button = ({ className, variant, size, asChild = false, ref, ...props }: ButtonProps) => {
	const Comp = asChild ? Slot : 'button'

	return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
}

export { Button, buttonVariants }
